<template>
  <el-dialog
      :model-value="props.visible"
      :modal-class="`my-drag-dialog ${props.modalClass}`"
      :style="{
              left: dialogPosition.x + 'px',
              top: dialogPosition.y + 'px',
              width: dialogPosition.width + 'px',
              height: dialogPosition.height + 'px',
              margin: '0',
              position: 'fixed'
      }"
      :modal-penetrable="modalPenetrable && !resizeState.isResizing"
      @closed="closedModal"
      @mousedown="handleMouseDown"
  >
    <template #header>
      <div class="draggable-header" @mousedown="startDrag">
        <slot name="header"></slot>
      </div>

    </template>

    <div class="dialog-content">
      <slot></slot>
    </div>

    <!-- 添加8个缩放控制点 -->
    <div v-for="handle in resizeHandles"
         :key="handle.position"
         class="resize-handle"
         :class="['resize-' + handle.position]"
         @mousedown="startResize($event, handle.position)"
    ></div>
  </el-dialog>
</template>

<script setup>
/*
 * 拖拽弹层，el-dialog套娃，已插槽化，可复用
 */
const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  modalClass: {
    type: String,
    default: ''
  },
  draggable: {
    type: Boolean,
    default: false
  },
  modalPenetrable: {
    type: Boolean,
    default: false
  },
  resetAfterClose: {
    type: Boolean,
    default: false
  },
  modalDefaultHeight: { // 缩放弹层默认的高度
    type: Number,
    default: 400
  },
  dialogPosition: {
    type: Object,
    default() {
      return {};
    }
  },
  modalDefaultWidth: { // 缩放弹层默认的宽度
    type: Number,
    default: 600
  }
})

const emit = defineEmits(['update:visible'])

function closedModal() {
  emit('update:visible', false)
  if (props.resetAfterClose) {
    Object.assign(dialogPosition, {
      x: Math.max((window.innerWidth - props.modalDefaultWidth) / 2, 50),
      y: Math.max((window.innerHeight - props.modalDefaultHeight) / 2, 50),
      width: props.modalDefaultWidth,
      height: props.modalDefaultHeight,
      minWidth: 400,
      minHeight: 300,
      ...props.dialogPosition,
    });
  }
}

// 弹窗位置和尺寸
const dialogPosition = reactive({
  x: Math.max((window.innerWidth - props.modalDefaultWidth) / 2, 50),
  y: Math.max((window.innerHeight - props.modalDefaultHeight) / 2, 50),
  width: props.modalDefaultWidth,
  height: props.modalDefaultHeight,
  minWidth: 400,
  minHeight: 300,
  ...props.dialogPosition,
});

// 8个缩放控制点
const resizeHandles = ref([
  {position: 'top'},
  {position: 'bottom'},
  {position: 'left'},
  {position: 'right'},
  {position: 'top-left'},
  {position: 'top-right'},
  {position: 'bottom-left'},
  {position: 'bottom-right'}
]);

// 缩放状态
const resizeState = reactive({
  isResizing: false,
  direction: '',
  startX: 0,
  startY: 0,
  startWidth: 0,
  startHeight: 0,
  startLeft: 0,
  startTop: 0
});

// 开始缩放
const startResize = (e, direction) => {
  if (e.button !== 0) return; // 只响应左键

  e.preventDefault();
  e.stopPropagation();

  resizeState.isResizing = true;
  resizeState.direction = direction;
  resizeState.startX = e.clientX;
  resizeState.startY = e.clientY;
  resizeState.startWidth = dialogPosition.width;
  resizeState.startHeight = dialogPosition.height;
  resizeState.startLeft = dialogPosition.x;
  resizeState.startTop = dialogPosition.y;

  document.addEventListener('mousemove', onResize);
  document.addEventListener('mouseup', stopResize);
};

// 缩放中
const onResize = (e) => {
  if (!resizeState.isResizing) return;

  const dx = e.clientX - resizeState.startX;
  const dy = e.clientY - resizeState.startY;

  let newWidth = resizeState.startWidth;
  let newHeight = resizeState.startHeight;
  let newX = resizeState.startLeft;
  let newY = resizeState.startTop;

  // 根据方向调整尺寸和位置
  switch (resizeState.direction) {
    case 'top':
      newHeight = Math.max(dialogPosition.minHeight, resizeState.startHeight - dy);
      newY = resizeState.startTop + (resizeState.startHeight - newHeight);
      break;
    case 'bottom':
      newHeight = Math.max(dialogPosition.minHeight, resizeState.startHeight + dy);
      break;
    case 'left':
      newWidth = Math.max(minWidthEnabled.value ? dialogPosition.minWidth : 100, resizeState.startWidth - dx);
      newX = resizeState.startLeft + (resizeState.startWidth - newWidth);
      break;
    case 'right':
      newWidth = Math.max(minWidthEnabled.value ? dialogPosition.minWidth : 100, resizeState.startWidth + dx);
      break;
    case 'top-left':
      newWidth = Math.max(minWidthEnabled.value ? dialogPosition.minWidth : 100, resizeState.startWidth - dx);
      newHeight = Math.max(dialogPosition.minHeight, resizeState.startHeight - dy);
      newX = resizeState.startLeft + (resizeState.startWidth - newWidth);
      newY = resizeState.startTop + (resizeState.startHeight - newHeight);
      break;
    case 'top-right':
      newWidth = Math.max(minWidthEnabled.value ? dialogPosition.minWidth : 100, resizeState.startWidth + dx);
      newHeight = Math.max(dialogPosition.minHeight, resizeState.startHeight - dy);
      newY = resizeState.startTop + (resizeState.startHeight - newHeight);
      break;
    case 'bottom-left':
      newWidth = Math.max(minWidthEnabled.value ? dialogPosition.minWidth : 100, resizeState.startWidth - dx);
      newHeight = Math.max(dialogPosition.minHeight, resizeState.startHeight + dy);
      newX = resizeState.startLeft + (resizeState.startWidth - newWidth);
      break;
    case 'bottom-right':
      newWidth = Math.max(minWidthEnabled.value ? dialogPosition.minWidth : 100, resizeState.startWidth + dx);
      newHeight = Math.max(dialogPosition.minHeight, resizeState.startHeight + dy);
      break;
  }

  // 边界检查
  if (newX < 0) {
    newWidth += newX;
    newX = 0;
  }

  if (newY < 0) {
    newHeight += newY;
    newY = 0;
  }

  if (newX + newWidth > window.innerWidth) {
    newWidth = window.innerWidth - newX;
  }

  if (newY + newHeight > window.innerHeight) {
    newHeight = window.innerHeight - newY;
  }

  // 确保最小尺寸
  newWidth = Math.max(minWidthEnabled.value ? dialogPosition.minWidth : 100, newWidth);
  newHeight = Math.max(dialogPosition.minHeight, newHeight);

  // 更新位置和尺寸
  dialogPosition.x = newX;
  dialogPosition.y = newY;
  dialogPosition.width = newWidth;
  dialogPosition.height = newHeight;
};

// 停止缩放
const stopResize = () => {
  resizeState.isResizing = false;
  document.removeEventListener('mousemove', onResize);
  document.removeEventListener('mouseup', stopResize);
};

// 是否启用最小宽度限制
const minWidthEnabled = ref(true);

// 防止点击对话框内容时触发拖拽
const handleMouseDown = (e) => {
  if (e.target.classList.contains('draggable-header')) {
    startDrag(e);
  }
};

// 拖拽状态
const dragState = reactive({
  isDragging: false,
  startX: 0,
  startY: 0,
  startLeft: 0,
  startTop: 0
});

// 开始拖拽
const startDrag = (e) => {
  if (e.button !== 0) return; // 只响应左键

  dragState.isDragging = true;
  dragState.startX = e.clientX;
  dragState.startY = e.clientY;
  dragState.startLeft = dialogPosition.x;
  dragState.startTop = dialogPosition.y;

  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
};

// 拖拽中
const onDrag = (e) => {
  if (!dragState.isDragging) return;

  const dx = e.clientX - dragState.startX;
  const dy = e.clientY - dragState.startY;

  // 计算新位置
  let newX = dragState.startLeft + dx;
  let newY = dragState.startTop + dy;

  // 边界检查
  newX = Math.max(0, Math.min(newX, window.innerWidth - dialogPosition.width));
  newY = Math.max(0, Math.min(newY, window.innerHeight - dialogPosition.height));

  // 更新位置
  dialogPosition.x = newX;
  dialogPosition.y = newY;
};

// 停止拖拽
const stopDrag = () => {
  dragState.isDragging = false;
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
};
</script>


<style lang="scss">
.my-drag-dialog {

  .draggable-header {
    cursor: move;
    user-select: none;
  }

  .resize-handle { /*隐藏式的大小触发器*/
    position: absolute;
    background: transparent;
    z-index: 100;
  }

  .resize-top {
    top: -4px;
    left: 10px;
    right: 10px;
    height: 8px;
    cursor: ns-resize;
  }

  .resize-bottom {
    bottom: -4px;
    left: 10px;
    right: 10px;
    height: 8px;
    cursor: ns-resize;
  }

  .resize-left {
    left: -4px;
    top: 10px;
    bottom: 10px;
    width: 8px;
    cursor: ew-resize;
  }

  .resize-right {
    right: -4px;
    top: 10px;
    bottom: 10px;
    width: 8px;
    cursor: ew-resize;
  }

  .resize-top-left {
    top: -8px;
    left: -8px;
    width: 16px;
    height: 16px;
    cursor: nwse-resize;
  }

  .resize-top-right {
    top: -8px;
    right: -8px;
    width: 16px;
    height: 16px;
    cursor: nesw-resize;
  }

  .resize-bottom-left {
    bottom: -8px;
    left: -8px;
    width: 16px;
    height: 16px;
    cursor: nesw-resize;
  }

  .resize-bottom-right {
    bottom: -8px;
    right: -8px;
    width: 16px;
    height: 16px;
    cursor: nwse-resize;
  }

  .dialog-content {
    height: 100%;
    /*max-height: 60vh;
    overflow-y: auto;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 8px;
    margin: 15px 0;*/
  }

  .el-dialog {
    display: flex;
    flex-direction: column;
    .el-dialog__body {
      height: 0;
      flex-grow: 1;
    }
  }
}
</style>
